import React, { useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Navigate, useParams } from 'react-router-dom';
import { Link as RouterLink } from '~/lib/react-router-dom';

import { Cancel, CheckCircle, ContentCopy, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';

import DefinitionText from '~/components/Common/Elements/DefinitionText';
import DefinitionTitle from '~/components/Common/Elements/DefinitionTitle';

import { propertyRoomsQueryKeys, useGetPropertyRooms } from '~/hooks/apis/accommodation/useGetPropertyRooms';

import { RoomResponse } from '~/services/AccommodationService';

import RoomAmenitiesGrid from './sections/AccommodationPropertyRoomAmenities/RoomAmenitiesGrid';
import AccommodationPropertyRoomImagesPanel from './sections/AccommodationPropertyRoomImages/AccommodationPropertyRoomImagesPanel';
import {
  AccommodationPropertyRoomContentTab,
  AccommodationPropertyRoomDetailsTab,
  AccommodationPropertyRoomPackagesTab,
} from './sections/AccommodationPropertyRooms/AccommodationPropertyRoomViewDetail';

const ROOM_TABS = [
  { value: 'details', label: 'Room details' },
  { value: 'content', label: 'Content' },
  { value: 'packages', label: 'Packages' },
  { value: 'room-images', label: 'Room images' },
  { value: 'amenities', label: 'Amenities' },
] as const;

type RoomTab = (typeof ROOM_TABS)[number]['value'];

function isRoomTab(value: string | undefined): value is RoomTab {
  return ROOM_TABS.some((tab) => tab.value === value);
}

function isRoomVisible(room: RoomResponse) {
  return room.visibility === 'auto_enabled' || room.visibility === 'force_enabled';
}

export default function AccommodationPropertyRoomDetailsPage() {
  const { propertyId, roomId, roomTab } = useParams<{ propertyId: string; roomId: string; roomTab?: string }>();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const {
    data: rooms = [],
    isLoading,
    error,
  } = useGetPropertyRooms({
    propertyId: propertyId ?? '',
    enabled: !!propertyId,
  });

  const room = useMemo(() => rooms.find((currentRoom) => currentRoom.id === roomId), [roomId, rooms]);
  const baseUrl = `/accommodation/properties/${propertyId}/rooms/${roomId}`;

  if (!roomTab || !isRoomTab(roomTab)) {
    return <Navigate to={`${baseUrl}/details`} replace />;
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">Error loading room: {error instanceof Error ? error.message : 'Unknown error'}</Alert>
    );
  }

  if (!room) {
    return <Alert severity="warning">Room not found.</Alert>;
  }

  const handleRoomUpdated = (updatedRoom: RoomResponse) => {
    queryClient.setQueryData<Array<RoomResponse>>(propertyRoomsQueryKeys.detail(propertyId!), (currentRooms) =>
      currentRooms?.map((currentRoom) => (currentRoom.id === updatedRoom.id ? updatedRoom : currentRoom)),
    );
  };

  const handleCopyRoomId = () => {
    navigator.clipboard.writeText(room.id);
    enqueueSnackbar('Room ID copied to clipboard', { variant: 'success' });
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Typography variant="h6">{room.name || `Room ${room.id}`}</Typography>
        <Chip
          icon={room.hasActiveLpcPackage ? <CheckCircle /> : <Cancel />}
          label={room.hasActiveLpcPackage ? 'Active LPC Package' : 'No Active LPC Package'}
          color={room.hasActiveLpcPackage ? 'success' : 'error'}
          variant="outlined"
        />
        {room.visibility !== undefined && (
          <Tooltip title="When hidden, the room will not be in content and no rates will be shown unless all rates = true">
            <Chip
              icon={isRoomVisible(room) ? <Visibility /> : <VisibilityOff />}
              label={isRoomVisible(room) ? 'Visible' : 'Hidden'}
              color={isRoomVisible(room) ? 'success' : 'error'}
              variant="outlined"
            />
          </Tooltip>
        )}
      </Stack>

      <Card>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            }}
          >
            <Box>
              <DefinitionTitle>Room ID</DefinitionTitle>
              <DefinitionText>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
                    {room.id}
                  </Typography>
                  <Tooltip title="Copy Room ID">
                    <IconButton size="small" onClick={handleCopyRoomId}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </DefinitionText>
            </Box>

            <Box>
              <DefinitionTitle>Suppliers</DefinitionTitle>
              <DefinitionText>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {(room.suppliers ?? []).map((supplier) => (
                    <Chip key={supplier} label={supplier} size="small" variant="outlined" color="primary" />
                  ))}
                </Stack>
              </DefinitionText>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs orientation="horizontal" variant="scrollable" aria-label="Room tabs" value={roomTab}>
          {ROOM_TABS.map((tab) => (
            <Tab
              key={tab.value}
              label={tab.label}
              value={tab.value}
              component={RouterLink}
              to={`${baseUrl}/${tab.value}`}
            />
          ))}
        </Tabs>
      </Box>

      {roomTab === 'details' && (
        <AccommodationPropertyRoomDetailsTab
          roomData={room}
          propertyId={propertyId!}
          onRoomUpdated={handleRoomUpdated}
        />
      )}
      {roomTab === 'content' && <AccommodationPropertyRoomContentTab roomData={room} />}
      {roomTab === 'packages' && <AccommodationPropertyRoomPackagesTab roomData={room} />}
      {roomTab === 'room-images' && (
        <AccommodationPropertyRoomImagesPanel roomId={room.id} roomName={room.name || room.id} isExpanded />
      )}
      {roomTab === 'amenities' && (
        <RoomAmenitiesGrid propertyId={propertyId!} roomId={room.id} roomName={room.name} isExpanded />
      )}
    </Stack>
  );
}
